import { PlusRegularIcon } from "@elixir/icons";
import { useReceiptStore } from "@elixir/stores";
import {
  DISCOUNT_TYPE,
  LocalServiceEntity,
  RECEIPT_DISCOUNT_TYPES,
  TSelectedSkuItem,
} from "@elixir/types";
import {
  calculateReceiptBreakdown,
  calculateReceiptPaymode,
  getDefaultReceiptService,
  getPositiveNumber,
  restrictToTwoDecimals,
  validateOneServiceEntity,
} from "@elixir/utils";
import { TableUi } from "ui";
import ServiceTableRow from "../receipt-v2/service-table-row";

export default function ServicesTable() {
  const services = useReceiptStore((state) => state.services);
  const setServices = useReceiptStore((state) => state.setServices);
  const additionalDiscountConfig = useReceiptStore(
    (state) => state.additionalDiscountConfig
  );
  const paymodes = useReceiptStore((state) => state.paymodes);
  const setPaymodes = useReceiptStore((state) => state.setPaymodes);

  const updatePaymodeOnServiceUpdate = (
    updatedServices: LocalServiceEntity[]
  ) => {
    const { grandTotal } = calculateReceiptBreakdown({
      services: updatedServices,
      additionalDiscountConfig,
      paymodes,
    });

    setPaymodes(calculateReceiptPaymode({ grandTotal, paymodes }));
  };

  const onServiceSelect = (
    serviceIndex: number
  ): (({ service }: { service: TSelectedSkuItem }) => void) => {
    return function ({ service }) {
      let _service = services[serviceIndex];
      _service = {
        ..._service,
        id: service.service_id,
        name: service.service_name,
        cost: {
          ..._service.cost,
          price: service.price,
          priceToShowWithType: service.price?.toString() || "",
        },
      };

      const updatedServices = [
        ...services.slice(0, serviceIndex),
        _service,
        ...services.slice(serviceIndex + 1),
      ];

      setServices(updatedServices);
      updatePaymodeOnServiceUpdate(updatedServices);
    };
  };

  const onServiceQuantityChange = (
    serviceIndex: number
  ): React.ChangeEventHandler<HTMLInputElement> => {
    return function (e) {
      e.target.value = getPositiveNumber(e.target.value);

      let _service = services[serviceIndex];
      _service = {
        ..._service,
        cost: {
          ..._service?.cost,
          qty: parseFloat(e.target.value),
        },
      };

      const updatedServices = [
        ...services.slice(0, serviceIndex),
        _service,
        ...services.slice(serviceIndex + 1),
      ];

      setServices(updatedServices);
      updatePaymodeOnServiceUpdate(updatedServices);
    };
  };

  const onAmountChange = (
    serviceIndex: number
  ): React.ChangeEventHandler<HTMLInputElement> => {
    return function (e) {
      let value = e.target.value.replace(/[^0-9.|-]/g, "");
      value = restrictToTwoDecimals(value);

      if (
        value &&
        value.length > 0 &&
        isNaN(parseFloat(value)) &&
        value !== "-"
      ) {
        return;
      }

      if (
        value &&
        value.length > 0 &&
        isNaN(parseFloat(value)) &&
        value !== "-"
      ) {
        return;
      }

      let _service = services[serviceIndex];
      _service = {
        ..._service,
        cost: {
          ..._service?.cost,
          priceToShowWithType: value,
          price: value === "-" ? -0 : parseFloat(value) || 0,
        },
      };

      const updatedServices = [
        ...services.slice(0, serviceIndex),
        _service,
        ...services.slice(serviceIndex + 1),
      ];
      setServices(updatedServices);
      updatePaymodeOnServiceUpdate(updatedServices);
    };
  };

  const onDiscountChange = (
    serviceIndex: number
  ): React.ChangeEventHandler<HTMLInputElement> => {
    return function (e) {
      let _service = services[serviceIndex];
      let discountToShowWithType = e.target.value
        .replace(/[^0-9.%]/g, "")
        .replace(/,/g, "");

      discountToShowWithType = discountToShowWithType.replace(/^0+/, "");

      const numericPart = discountToShowWithType.replace("%", "");
      const restrictedNumeric = restrictToTwoDecimals(numericPart);

      discountToShowWithType = e.target.value.includes("%")
        ? restrictedNumeric + "%"
        : restrictedNumeric;

      if (e.target.value.includes("%")) {
        //If Percent is the only thing typed or Percent is not last in the array return
        if (
          e.target.value.length === 1 ||
          e.target.value.indexOf("%") < e.target.value.length - 1
        )
          return;

        _service = {
          ..._service,
          cost: {
            ..._service?.cost,
            discountType: RECEIPT_DISCOUNT_TYPES.find(
              (dt) => dt.id === DISCOUNT_TYPE.PERCENTAGE
            )!,
            discountToShowWithType,
            discount: parseFloat(restrictedNumeric),
          },
        };
      } else {
        _service = {
          ..._service,
          cost: {
            ..._service?.cost,
            discountType: RECEIPT_DISCOUNT_TYPES.find(
              (dt) => dt.id === DISCOUNT_TYPE.AMOUNT
            )!,
            discountToShowWithType,
            discount: parseFloat(restrictedNumeric),
          },
        };
      }

      const updatedServices = [
        ...services.slice(0, serviceIndex),
        _service,
        ...services.slice(serviceIndex + 1),
      ];
      setServices(updatedServices);
      updatePaymodeOnServiceUpdate(updatedServices);
    };
  };

  const onRemoveService = (serviceIndex: number) => {
    return () => {
      const updatedServices = services.filter(
        (item, idx) => idx !== serviceIndex
      );
      setServices(updatedServices);
      updatePaymodeOnServiceUpdate(updatedServices);
    };
  };

  return (
    <TableUi
      headersConfig={[
        {
          width: "40%",
          headerName: "Service",
        },
        {
          width: "12%",
          headerName: "Qty",
        },
        {
          width: "14%",
          headerName: "Amount",
        },
        {
          width: "16%",
          headerName: "Discount",
        },
        {
          width: "15%",
          headerName: "Total",
        },
      ]}
      footer={
        <button
          className="dr-leading-20 dr-font-700 dr-text-13 dr-text-darwin-primary-primary dr-cursor-pointer dr-max-w-max dr-flex dr-items-center"
          onClick={() => {
            setServices([...services, getDefaultReceiptService({})]);
          }}
        >
          <PlusRegularIcon className="dr-w-14 dr-h-14 dr-mr-4" /> Add another
        </button>
      }
    >
      {services.map((service, index) => {
        const { errorKey } = validateOneServiceEntity(service);
        return (
          <ServiceTableRow
            key={index}
            onServiceSelect={onServiceSelect(index)}
            allowRemoveService={!!services.length && services.length > 1}
            service={service}
            index={index}
            onQtyChange={onServiceQuantityChange(index)}
            onAmountChange={onAmountChange(index)}
            onDiscountChange={onDiscountChange(index)}
            onRemoveService={onRemoveService(index)}
            amountError={errorKey === "S_AMOUNT"}
            discountError={errorKey === "S_DISCOUNT"}
            quantityError={errorKey === "S_QUANTITY"}
          />
        );
      })}
    </TableUi>
  );
}
